import { DraggableLocation, DropResult } from '@hello-pangea/dnd';

type ValidDropResult = DropResult & { destination: DraggableLocation };

export const isValidDrop = (result: DropResult): result is ValidDropResult =>
  result.destination !== null && result.destination !== undefined;

export const isSamePosition = ({ source, destination }: DropResult) => {
  return (
    source.index === destination?.index &&
    source.droppableId === destination?.droppableId
  );
};

type MoveItemsArgs<T> = {
  sourceArray: T[];
  destinationArray?: T[];
  sourceIndex: number;
  destinationIndex: number;
};

const moveItems = <T>({
  sourceArray,
  destinationArray,
  sourceIndex,
  destinationIndex,
}: MoveItemsArgs<T>) => {
  const newSourceArray = Array.from(sourceArray);
  const newDestinationArray = destinationArray
    ? Array.from(destinationArray)
    : newSourceArray;
  const [item] = newSourceArray.splice(sourceIndex, 1);
  newDestinationArray.splice(destinationIndex, 0, item);
  return { sourceArray: newSourceArray, destinationArray: newDestinationArray };
};

type NestedDropResult<P, C> = {
  parents: P[];
  childrenMap: Record<string, C[]>;
} | null;

const handleSameGroupReorder = <T>(
  result: ValidDropResult,
  items: T[],
) => {
  const { source, destination } = result;
  const { destinationArray: reordered } = moveItems({
    sourceArray: items,
    sourceIndex: source.index,
    destinationIndex: destination.index,
  });
  return reordered;
};

const handleDifferentGroupReorder = <P, C>(
  result: ValidDropResult,
  parents: P[],
  childrenMap: Record<string, C[]>,
) => {
  const { source, destination } = result;
  const sourceChildren = childrenMap[source.droppableId] || [];
  const destChildren = childrenMap[destination.droppableId] || [];

  const { destinationArray, sourceArray } = moveItems({
    sourceArray: sourceChildren,
    destinationArray: destChildren,
    sourceIndex: source.index,
    destinationIndex: destination.index,
  });

  return {
    parents,
    childrenMap: {
      ...childrenMap,
      [source.droppableId]: sourceArray,
      [destination.droppableId]: destinationArray,
    },
  };
};

export const nestedDropHandler = <P, C>(
  result: DropResult,
  parents: P[],
  childrenMap: Record<string, C[]>,
  parentType: string,
): NestedDropResult<P, C> => {
  const { source, type } = result;
  if (!isValidDrop(result) || isSamePosition(result)) return null;

  if (type === parentType) {
    const reordered = handleSameGroupReorder(result, parents);
    return { parents: reordered, childrenMap };
  }

  if (source.droppableId === result.destination.droppableId) {
    const children = childrenMap[source.droppableId] || [];
    const reordered = handleSameGroupReorder(result, children);
    return {
      parents,
      childrenMap: {
        ...childrenMap,
        [source.droppableId]: reordered,
      },
    };
  }

  return handleDifferentGroupReorder(result, parents, childrenMap);
};

export const dropHandler = <T>(dndResult: DropResult, originalArray: T[]) => {
  if (!isValidDrop(dndResult) || isSamePosition(dndResult))
    return originalArray;

  return handleSameGroupReorder(dndResult, originalArray);
};
